import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Wallet, Percent, Ticket } from "lucide-react";
import { api, apiError, eur } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

const Stat = ({ icon, label, value, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 ambient" data-testid={testId}>
    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400">{icon} {label}</p>
    <p className="mt-2 font-display text-2xl text-slate-900">{value}</p>
  </div>
);

export default function AgencyDashboard() {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/agency/summary").then(({ data }) => setS(data)).catch((e) => { setErr(true); toast.error(apiError(e)); });
  }, []);

  const num = (...keys) => { for (const k of keys) if (s?.[k] != null) return s[k]; return null; };
  const bookings = Array.isArray(s?.bookings) ? s.bookings : Array.isArray(s?.recent) ? s.recent : [];
  const creditLimit = num("credit_limit");
  const creditUsed = num("credit_used", "used_credit");
  const creditRemaining = num("credit_remaining") ?? (creditLimit != null && creditUsed != null ? creditLimit - creditUsed : null);
  const discount = num("discount_pct", "discount");
  const totalBookings = num("bookings_count", "total_bookings", "count") ?? bookings.length;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-5xl px-5 py-12 lg:px-10 lg:py-16">
        <p className="label-eyebrow">{t("agency.title")}</p>
        <h1 className="mt-2 font-display text-3xl tracking-tight sm:text-4xl" data-testid="agency-company">
          {s?.company || s?.name || t("agency.title")}
        </h1>

        {err ? (
          <p className="mt-8 text-slate-500" data-testid="agency-error">{t("common.error")}</p>
        ) : !s ? (
          <p className="mt-8 text-slate-500" data-testid="agency-loading">{t("common.loading")}</p>
        ) : (
          <>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat icon={<Ticket size={13} />} label={t("agency.bookings")} value={totalBookings} testId="agency-stat-bookings" />
              {discount != null && <Stat icon={<Percent size={13} />} label={t("agency.discount")} value={`${discount}%`} testId="agency-stat-discount" />}
              {creditLimit != null && <Stat icon={<Wallet size={13} />} label={t("agency.creditLimit")} value={eur(creditLimit)} testId="agency-stat-credit-limit" />}
              {creditRemaining != null && <Stat icon={<Building2 size={13} />} label={t("agency.creditRemaining")} value={eur(creditRemaining)} testId="agency-stat-credit-remaining" />}
            </div>

            <section className="mt-8 rounded-xl border border-slate-200 bg-white p-6 ambient">
              <p className="label-eyebrow">{t("agency.recent")}</p>
              <div className="mt-4 space-y-2" data-testid="agency-bookings-list">
                {bookings.length === 0 && <p className="text-sm text-slate-400">{t("common.empty")}</p>}
                {bookings.map((b, i) => (
                  <div key={b.booking_id || b.code || i} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 py-2 text-sm last:border-0">
                    <span className="text-slate-700">{b.code} · {b.origin?.name || "?"} → {b.destination?.name || "?"}</span>
                    <span className="flex items-center gap-3">
                      <span className="text-xs text-slate-400">{(b.pickup_at || "").replace("T", " · ").slice(0, 16)}</span>
                      <span className="font-medium">{eur(b.price_eur)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        <Link to="/" className="mt-8 inline-block text-sm font-semibold text-sea underline">{t("del.backHome")}</Link>
      </div>
      <Footer />
    </div>
  );
}
